import { Susd3CooldownStatusAbi } from '../abis/susd3'
import type { LstWithdrawalRequest } from '../types'
import { computeStatus, toBigInt, type LstWithdrawalReader } from './shared'

/**
 * 3Jane sUSD3 cooldown reader.
 *
 * One call: `getCooldownStatus(user) → (cooldownEnd, windowEnd,
 * shares)`. Non-zero `shares` means an open cooldown request; the claim
 * is a plain ERC-4626 `redeem` on the vault once `cooldownEnd` passes
 * and before `windowEnd` (the window read ~100 years out at
 * integration, so `expired` is theoretical). The shares never leave the
 * owner's balance and `cancelCooldown()` abandons the request.
 *
 * A follow-up `convertToAssets(shares)` denominates the amount in USD3;
 * `shares` is also surfaced raw because the claim (`redeem`) takes the
 * share amount.
 */
export const readerSusd3Cooldown: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    const res = (await multicallRetry({
      chain: chainId,
      calls: [
        { address: entry.lst, name: 'getCooldownStatus', params: [user] },
      ] as unknown as never[],
      abi: [Susd3CooldownStatusAbi] as unknown as never,
    })) as unknown[]

    const cell = res[0] as
      | [unknown, unknown, unknown]
      | { cooldownEnd?: unknown; windowEnd?: unknown; shares?: unknown }
      | undefined
    let cooldownEnd: bigint | undefined
    let windowEnd: bigint | undefined
    let shares: bigint | undefined
    if (Array.isArray(cell)) {
      cooldownEnd = toBigInt(cell[0])
      windowEnd = toBigInt(cell[1])
      shares = toBigInt(cell[2])
    } else if (cell && typeof cell === 'object') {
      cooldownEnd = toBigInt(cell.cooldownEnd)
      windowEnd = toBigInt(cell.windowEnd)
      shares = toBigInt(cell.shares)
    }

    if (!shares || shares === 0n) return []

    let amount = shares
    try {
      const stage2 = (await multicallRetry({
        chain: chainId,
        calls: [
          { address: entry.lst, name: 'convertToAssets', params: [shares] },
        ] as unknown as never[],
        abi: [Susd3CooldownStatusAbi] as unknown as never,
      })) as unknown[]
      amount = toBigInt(stage2[0]) ?? shares
    } catch {
      // keep the share-denominated amount
    }

    const readyAt = Number(cooldownEnd ?? 0n)
    const expiresAt =
      windowEnd && windowEnd > 0n && windowEnd < 10n ** 12n
        ? Number(windowEnd)
        : undefined

    const out: LstWithdrawalRequest[] = [
      {
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: '0',
        amountUnderlying: amount.toString(),
        shares: shares.toString(),
        status: computeStatus(readyAt, expiresAt),
        readyAt,
        ...(expiresAt !== undefined ? { expiresAt } : {}),
      },
    ]
    return out
  },
}
